import React, { useState, useMemo } from "react";
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Card,
  CardBody,
  SimpleGrid,
  Button,
  Select,
  Input,
  Spinner,
  useToast,
  Flex,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
} from "@chakra-ui/react";
import { FiDownload, FiRefreshCw, FiCalendar, FiUsers, FiClock, FiMapPin } from "react-icons/fi";
import ReportTable from "../../components/tables/ReportTable";
import { useGetAttendanceReports, useGetAdminAttendanceReports, useTriggerAutoClockout } from "../../services/reportService";
import { useUserContext } from "../../context/UserContext";
import BranchFilter from "../../components/BranchFilter";

const AttendanceReport = () => {
  const [query, setQuery] = useState({
    date: new Date().toISOString().split('T')[0],
    employee_id: "",
  });
  const [selectedBranch, setSelectedBranch] = useState("all");
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
  });
  const toast = useToast();
  const { user } = useUserContext();

  // Role-based endpoint selection
  const isCompanyAdmin = user?.role === 'company_admin';

  // Auto clock-out trigger hook (admin only)
  const { triggerAutoClockoutAsync, triggerAutoClockoutIsLoading } = useTriggerAutoClockout();
  
  // Company admins: use admin endpoints with branch filtering
  const adminAttendanceData = useGetAdminAttendanceReports({ 
    enabled: isCompanyAdmin,
    branch_id: selectedBranch
  });
  
  // Branch users: use regular endpoints (no branch filtering needed)
  const regularAttendanceData = useGetAttendanceReports({ 
    enabled: !isCompanyAdmin
  });
  
  // Select appropriate data based on user role
  const attendanceIsLoading = isCompanyAdmin 
    ? adminAttendanceData.adminAttendanceIsLoading 
    : regularAttendanceData.attendanceIsLoading;
    
  const attendanceData = isCompanyAdmin 
    ? adminAttendanceData.adminAttendanceData 
    : regularAttendanceData.attendanceData;
    
  const attendancePagination = isCompanyAdmin 
    ? adminAttendanceData.adminAttendancePagination 
    : regularAttendanceData.attendancePagination;
    
  const attendanceSetFilter = isCompanyAdmin 
    ? adminAttendanceData.adminAttendanceSetFilter 
    : regularAttendanceData.attendanceSetFilter;

  // Set initial filter on component mount
  React.useEffect(() => {
    attendanceSetFilter({
      date: query.date,
      employee_id: query.employee_id,
      page: pagination.page,
      limit: pagination.limit,
    });
  }, []); // Only run once on mount

  // Auto-apply filters when branch selection changes
  React.useEffect(() => {
    if (isCompanyAdmin) {
      // Reset pagination when branch changes
      setPagination(prev => ({ ...prev, page: 1 }));
      
      const filterParams = { 
        ...query,
        page: 1, // Always start from page 1 when branch changes
        limit: pagination.limit,
      };
      
      // Add branch filter for company admins
      if (selectedBranch && selectedBranch !== 'all') {
        filterParams.branch_id = selectedBranch;
      }
      
      
      
      attendanceSetFilter(filterParams);
    }
  }, [selectedBranch, isCompanyAdmin]); // Trigger when branch changes



  const handleFilter = () => {
    const filterParams = { 
      ...query,
      page: pagination.page,
      limit: pagination.limit,
    };
    
    // Add branch filter for company admins
    if (isCompanyAdmin && selectedBranch && selectedBranch !== 'all') {
      filterParams.branch_id = selectedBranch;
    }
    
    attendanceSetFilter(filterParams);
  };

  const handleClearFilters = () => {
    setQuery({
      date: new Date().toISOString().split('T')[0],
      employee_id: "",
    });
    setPagination({
      page: 1,
      limit: 20,
    });
    attendanceSetFilter({
      date: new Date().toISOString().split('T')[0],
      employee_id: "",
      page: 1,
      limit: 20,
    });
  };

  const handleExport = () => {
    toast({
      title: "Export initiated",
      description: "Attendance report export has been started. You will receive a notification when ready.",
      status: "info",
      duration: 3000,
      isClosable: true,
      position: "top",
    });
  };

  const handleRefresh = () => {
    if (isCompanyAdmin) {
      // Trigger auto clock-out job first, then refresh data
      triggerAutoClockoutAsync()
        .then((response) => {
          const results = response?.results || response?.data?.results;
          toast({
            title: "Auto Clock-Out Completed",
            description: results
              ? `Clocked out: ${results.auto_clocked_out_count || 0}, Skipped: ${results.skipped_count || 0}, Errors: ${results.error_count || 0}`
              : "Auto clock-out job completed successfully",
            status: "success",
            duration: 5000,
            isClosable: true,
            position: "top",
          });
          
          // Refresh attendance data after auto clock-out
          adminAttendanceData.refetchAdminAttendance();
        })
        .catch((error) => {
          toast({
            title: "Auto Clock-Out Failed",
            description: error?.message || "Failed to trigger auto clock-out job",
            status: "error",
            duration: 5000,
            isClosable: true,
            position: "top",
          });
          
          // Still refresh data even if auto clock-out fails
          adminAttendanceData.refetchAdminAttendance();
        });
    } else {
      // For branch users, just refresh data
      regularAttendanceData.refetchattendance();
    }
  };

  // Calculate attendance statistics
  const attendanceStats = useMemo(() => {
    if (!attendanceData?.length) return { total: 0, present: 0, absent: 0, late: 0, leave: 0 };
    
    let present = 0;
    let absent = 0;
    let late = 0;
    let leave = 0;
    
    attendanceData.forEach(record => {
      const status = record.attendance_status?.toLowerCase();
      if (status === 'present') present++;
      else if (status === 'absent') absent++;
      else if (status === 'late') late++;
      else if (status === 'leave' || status === 'on leave') leave++;
    });
    

    
    return {
      total: attendanceData.length,
      present,
      absent,
      late,
      leave
    };
  }, [attendanceData, selectedBranch, isCompanyAdmin]);

  return (
    <Box w="100%" bg="gray.50" minH="100vh" p={6}>
      {/* Header */}
      <VStack spacing={6} align="stretch">
        <HStack justify="space-between" align="center">
          <VStack align="start" spacing={2}>
            <Heading size="lg" color="gray.900" fontWeight="600">
              Attendance Report
            </Heading>
            <Text color="gray.600" fontSize="md">
              Monitor employee attendance patterns and records
            </Text>
          </VStack>
          <HStack spacing={3}>
            {isCompanyAdmin && (
              <BranchFilter
                selectedBranch={selectedBranch}
                onBranchChange={setSelectedBranch}
                showLabel={false}
                maxW="250px"
              />
            )}
            <Button
              leftIcon={<FiRefreshCw />}
              colorScheme="blue"
              variant="outline"
              onClick={handleRefresh}
              isLoading={triggerAutoClockoutIsLoading}
              loadingText="Running..."
            >
              Refresh
            </Button>
            <Button
              leftIcon={<FiDownload />}
              colorScheme="green"
              variant="outline"
              onClick={handleExport}
            >
              Export Report
            </Button>
          </HStack>
        </HStack>

        {/* Filters */}
        <Card>
          <CardBody>
            <VStack spacing={4} align="stretch">
              <Heading size="md" color="gray.900">
                Filters
              </Heading>
              <HStack spacing={4} wrap="wrap">
                <Input
                  type="date"
                  value={query.date}
                  onChange={(e) => setQuery({ ...query, date: e.target.value })}
                  flex={1}
                  minW="250px"
                />
                <Input
                  placeholder="Employee ID"
                  value={query.employee_id}
                  onChange={(e) => setQuery({ ...query, employee_id: e.target.value })}
                  flex={1}
                  minW="250px"
                />
                <Button colorScheme="blue" onClick={handleFilter}>
                  Apply Filters
                </Button>
                <Button variant="outline" onClick={handleClearFilters}>
                  Clear
                </Button>
              </HStack>
            </VStack>
          </CardBody>
        </Card>

        {/* Attendance Statistics */}
        <SimpleGrid columns={{ base: 1, md: 2, lg: 5 }} spacing={6}>
          <StatCard
            title="Total Employees"
            value={attendanceStats.total}
            color="blue"
            icon={<FiUsers />}
          />
          <StatCard
            title="Present Today"
            value={attendanceStats.present}
            color="green"
            icon={<FiCalendar />}
          />
          <StatCard
            title="Absent Today"
            value={attendanceStats.absent}
            color="red"
            icon={<FiClock />}
          />
          <StatCard
            title="Late Arrivals"
            value={attendanceStats.late}
            color="orange"
            icon={<FiMapPin />}
          />
          <StatCard
            title="On Leave"
            value={attendanceStats.leave}
            color="purple"
            icon={<FiCalendar />}
          />
        </SimpleGrid>

        {/* Attendance Table */}
        {attendanceIsLoading ? (
          <Card>
            <CardBody>
              <Box textAlign="center" py={8} w="100%">
                <Spinner size="lg" color="blue.500" />
                <Text mt={4} color="gray.600">Loading attendance data...</Text>
              </Box>
            </CardBody>
          </Card>
        ) : (
          <>
            <ReportTable
              employeeData={attendanceData}
              attendanceIsLoading={attendanceIsLoading}
            />
            
            {/* Pagination Controls */}
            <Card>
              <CardBody>
                <Flex justify="space-between" align="center" wrap="wrap" gap={4}>
                  <HStack spacing={4}>
                    <Text fontSize="sm" color="gray.600">
                      Total: {attendancePagination?.total_count || 0} records
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      Page Size:
                    </Text>
                    <NumberInput
                      size="sm"
                      min={5}
                      max={100}
                      value={pagination.limit}
                      onChange={(value) => {
                        setPagination(prev => ({ ...prev, limit: parseInt(value), page: 1 }));
                        handleFilter();
                      }}
                      w="80px"
                    >
                      <NumberInputField />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </HStack>
                  
                  <HStack spacing={2}>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        if (pagination.page > 1) {
                          setPagination(prev => ({ ...prev, page: prev.page - 1 }));
                          handleFilter();
                        }
                      }}
                      isDisabled={pagination.page <= 1}
                    >
                      Previous
                    </Button>
                    <Text fontSize="sm" color="gray.600" px={3}>
                      Page {pagination.page} of {attendancePagination?.total_pages || 1}
                    </Text>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setPagination(prev => ({ ...prev, page: prev.page + 1 }));
                        handleFilter();
                      }}
                      isDisabled={!attendanceData || pagination.page >= (attendancePagination?.total_pages || 1)}
                    >
                      Next
                    </Button>
                  </HStack>
                </Flex>
              </CardBody>
            </Card>
          </>
        )}
      </VStack>
    </Box>
  );
};

// Stat Card Component
const StatCard = ({ title, value, color, icon }) => {
  const colorScheme = {
    blue: { bg: "blue.50", color: "blue.600", border: "blue.200" },
    green: { bg: "green.50", color: "green.600", border: "green.200" },
    red: { bg: "red.50", color: "red.600", border: "red.200" },
    orange: { bg: "orange.50", color: "orange.600", border: "orange.200" },
    purple: { bg: "purple.50", color: "purple.600", border: "purple.200" },
  };

  const colors = colorScheme[color] || colorScheme.blue;

  return (
    <Card>
      <CardBody>
        <HStack spacing={4} align="center">
          <Box
            p={3}
            borderRadius="12px"
            bg={colors.bg}
            color={colors.color}
            border="1px solid"
            borderColor={colors.border}
          >
            {icon}
          </Box>
          <VStack spacing={1} align="start" flex={1}>
            <Text fontSize="sm" color="gray.600" fontWeight="500">
              {title}
            </Text>
            <Text fontSize="2xl" color="gray.900" fontWeight="700">
              {value}
            </Text>
          </VStack>
        </HStack>
      </CardBody>
    </Card>
  );
};

export default AttendanceReport; 